import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Plus, Video, Trash2, GripVertical, Upload, CheckCircle2,
  Loader2, AlertCircle, Pencil, Image, Eye, EyeOff, Lock, Settings2, Tag,
  Sparkles, PlayCircle, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const MAX_EPISODE_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB

// ───────────── Defaults for Phase C Course Builder ─────────────
const DEFAULT_TIERS = [
  { key: 'free_trial', enabled: false, label: 'Free preview', episode_count: 1, cta: 'Start free' },
  { key: 'paid_trial', enabled: true, label: 'Paid trial', episode_count: 2, cta: 'Try for ₹99', ops_sku: '' },
  { key: 'full', enabled: true, label: 'Full course', cta: 'Buy full', ops_sku: '' },
];
const DEFAULT_PACING = { strategy: 'immediate', drip_days: 3, min_progress_for_next_pct: 80 };
const DEFAULT_DISCOVERY = {
  is_listed: true,
  listed_in_audiences: ['prenatal', 'postnatal'],
  learner_type_target: 'all',
};
const DEFAULT_EXPIRY = { type: 'edd_plus_days', buffer_days: 5 };

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('episodes');

  // Editable form state (hydrated from course)
  const [meta, setMeta] = useState({});
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [pacing, setPacing] = useState(DEFAULT_PACING);
  const [discovery, setDiscovery] = useState(DEFAULT_DISCOVERY);
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  // V14.1 — unlock rule (immediate / week_gte / postnatal_days / date_gte)
  const [unlockRule, setUnlockRule] = useState({ type: 'immediate' });

  // Episode management state
  const [addEpDialog, setAddEpDialog] = useState(false);
  const [newEp, setNewEp] = useState({ title: '', description: '' });
  const [uploading, setUploading] = useState({});
  const [editEpDialog, setEditEpDialog] = useState({ open: false, ep: null });
  const [editEpForm, setEditEpForm] = useState({ title: '', description: '' });
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [simOpen, setSimOpen] = useState(false);
  const fileInputRefs = useRef({});
  const directUploadRef = useRef({});

  const fetchCourse = async () => {
    try {
      const res = await api.get(`/api/courses/${courseId}`);
      const c = res.data;
      setCourse(c);
      setMeta({
        title: c.title, description: c.description, course_type: c.course_type,
        trimester: c.trimester, trimester_tags: c.trimester_tags || [], access_level: c.access_level, status: c.status,
        ops_sku: c.ops_sku || '',
      });
      // Hydrate each config block, defaulting if missing
      const loadedTiers = Array.isArray(c.access_tiers) && c.access_tiers.length
        ? DEFAULT_TIERS.map((d) => ({
            ...d,
            ...(c.access_tiers.find((t) => t.key === d.key) || {}),
          }))
        : DEFAULT_TIERS;
      setTiers(loadedTiers);
      setPacing({ ...DEFAULT_PACING, ...(c.pacing || {}) });
      setDiscovery({ ...DEFAULT_DISCOVERY, ...(c.discovery || {}) });
      setExpiry({ ...DEFAULT_EXPIRY, ...(c.expiry_policy || {}) });
      setUnlockRule(c.unlock_rule || { type: 'immediate' });
    } catch { toast.error('Failed to load course'); }
    setLoading(false);
  };

  useEffect(() => { fetchCourse(); }, [courseId]);

  // V14.1 — On page load, auto-reconcile any episode stuck in encoding state.
  // Catches "encoding stuck for hours" bug when browser tab was closed mid-encode.
  useEffect(() => {
    if (!course) return;
    const stuck = (course.episodes || []).some((ep) => ep.status === 'encoding' || ep.status === 'uploading');
    if (!stuck) return;
    api.post(`/api/courses/${courseId}/reconcile-encoding`).then((res) => {
      if (res.data?.reconciled_count > 0) {
        toast.success(`Refreshed ${res.data.reconciled_count} episode${res.data.reconciled_count > 1 ? 's' : ''} from Bunny`);
        fetchCourse();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.id]);

  const refreshEncoding = async () => {
    try {
      const res = await api.post(`/api/courses/${courseId}/reconcile-encoding`);
      if (res.data?.reconciled_count > 0) {
        toast.success(`Updated ${res.data.reconciled_count} episode${res.data.reconciled_count > 1 ? 's' : ''}`);
      } else {
        toast.info('No status changes — Bunny still encoding');
      }
      fetchCourse();
    } catch { toast.error('Refresh failed'); }
  };

  const saveAll = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/api/courses/${courseId}`, payload);
      toast.success('Saved');
      fetchCourse();
    } catch (err) {
      // V14.5 Part 19z6 — Surface the actual backend error code/message
      // so admins know why a save failed (was silently "Save failed" before).
      const msg = err?.response?.data?.detail || err?.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    }
    setSaving(false);
  };

  const addEpisode = async () => {
    if (!newEp.title.trim()) { toast.error('Episode title required'); return; }
    try {
      const res = await api.post(`/api/courses/${courseId}/episodes`, newEp);
      const { episode, direct_upload } = res.data;
      if (direct_upload) directUploadRef.current[episode.id] = direct_upload;
      toast.success('Episode created — select a video file to upload');
      setAddEpDialog(false);
      setNewEp({ title: '', description: '' });
      fetchCourse();
      setTimeout(() => { fileInputRefs.current[episode.id]?.click(); }, 500);
    } catch { toast.error('Failed to create episode'); }
  };

  const uploadVideo = async (episodeId, bunnyGuid, file) => {
    if (file.size > MAX_EPISODE_SIZE) {
      toast.error(`File too large (${(file.size / 1024 / 1024 / 1024).toFixed(1)} GB). Max 2 GB.`);
      return;
    }
    setUploading((p) => ({ ...p, [episodeId]: { progress: 0, status: 'uploading' } }));
    try {
      const directInfo = directUploadRef.current[episodeId];
      let uploadUrl, headers;
      if (directInfo) {
        uploadUrl = directInfo.url;
        headers = directInfo.headers;
      } else {
        try {
          const infoRes = await api.get(`/api/courses/${courseId}/episodes/${episodeId}/upload-url`);
          uploadUrl = infoRes.data.url;
          headers = infoRes.data.headers;
        } catch {
          uploadUrl = `/api/courses/${courseId}/episodes/${episodeId}/upload`;
          headers = {};
        }
      }
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        if (uploadUrl.startsWith('/api')) {
          const token = localStorage.getItem('ss_token');
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploading((p) => ({ ...p, [episodeId]: { progress: pct, status: 'uploading' } }));
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });
      await api.put(`/api/courses/${courseId}/episodes/${episodeId}`, { status: 'encoding', file_size: file.size });
      setUploading((p) => ({ ...p, [episodeId]: { progress: 100, status: 'encoding' } }));
      toast.success('Upload complete! Bunny is encoding...');
      fetchCourse();
      pollEncoding(episodeId);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
      setUploading((p) => ({ ...p, [episodeId]: { progress: 0, status: 'failed' } }));
    }
  };

  const pollEncoding = async (episodeId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 15000));
      try {
        const res = await api.post(`/api/courses/${courseId}/episodes/${episodeId}/check-encoding`);
        if (res.data.status === 'ready') {
          setUploading((p) => ({ ...p, [episodeId]: { progress: 100, status: 'ready' } }));
          toast.success('Episode ready for playback!');
          fetchCourse();
          return;
        } else if (res.data.status === 'failed') {
          setUploading((p) => ({ ...p, [episodeId]: { progress: 0, status: 'failed' } }));
          toast.error('Encoding failed');
          fetchCourse();
          return;
        }
        setUploading((p) => ({ ...p, [episodeId]: { progress: res.data.encode_progress || 50, status: 'encoding' } }));
      } catch { break; }
    }
  };

  const deleteEpisode = async (episodeId) => {
    if (!window.confirm('Delete this episode?')) return;
    try { await api.delete(`/api/courses/${courseId}/episodes/${episodeId}`); toast.success('Deleted'); fetchCourse(); }
    catch { toast.error('Failed'); }
  };

  const saveEpisodeEdit = async () => {
    try {
      await api.put(`/api/courses/${courseId}/episodes/${editEpDialog.ep.id}`, editEpForm);
      toast.success('Episode updated');
      setEditEpDialog({ open: false, ep: null });
      fetchCourse();
    } catch { toast.error('Failed'); }
  };

  const uploadThumbnail = async (file) => {
    if (!file.type.startsWith('image/')) { toast.error('Only images'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setThumbnailUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/api/courses/${courseId}/thumbnail`, form);
      toast.success('Thumbnail uploaded');
      fetchCourse();
    } catch { toast.error('Failed'); }
    setThumbnailUploading(false);
  };

  const handleDragStart = (i) => setDraggedIdx(i);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === dropIdx) return;
    const eps = [...(course.episodes || [])];
    const [moved] = eps.splice(draggedIdx, 1);
    eps.splice(dropIdx, 0, moved);
    try {
      await api.put(`/api/courses/${courseId}/episodes/reorder`, { episode_ids: eps.map((ep) => ep.id) });
      fetchCourse();
    } catch { toast.error('Reorder failed'); }
    setDraggedIdx(null);
  };

  const getStatusIcon = (status) => {
    if (status === 'ready') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (status === 'encoding') return <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />;
    if (status === 'failed') return <AlertCircle className="h-4 w-4 text-rose-600" />;
    return <Upload className="h-4 w-4 text-muted-foreground" />;
  };

  const toggleStatus = async () => {
    const newStatus = course.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/api/courses/${courseId}`, { status: newStatus });
      toast.success(`Course ${newStatus}`);
      fetchCourse();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-60" /><Skeleton className="h-64 rounded-2xl" /><Skeleton className="h-96 rounded-2xl" /></div>;
  if (!course) return <p>Course not found</p>;

  const episodes = course.episodes || [];

  return (
    <div className="space-y-4 max-w-4xl" data-testid="course-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/courses')}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{course.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className={course.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{course.status}</Badge>
            <Badge variant="outline">{course.course_type}</Badge>
            {course.ops_sku && (<Badge variant="outline" className="text-[10px]"><Tag className="h-3 w-3 mr-1" />{course.ops_sku}</Badge>)}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSimOpen(true)} data-testid="preview-as-learner-btn">
          <PlayCircle className="h-4 w-4 mr-1" />Preview as learner
        </Button>
        <Button variant="outline" onClick={toggleStatus}>
          {course.status === 'published'
            ? <><EyeOff className="h-4 w-4 mr-2" />Unpublish</>
            : <><Eye className="h-4 w-4 mr-2" />Publish</>}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md" data-testid="course-builder-tabs">
          <TabsTrigger value="episodes" data-testid="tab-episodes">Episodes</TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access">
            <Lock className="h-3 w-3 mr-1" />Access & Pricing
          </TabsTrigger>
          <TabsTrigger value="pacing" data-testid="tab-pacing">
            <Sparkles className="h-3 w-3 mr-1" />Pacing & Discovery
          </TabsTrigger>
        </TabsList>

        {/* ───── TAB 1: EPISODES (+ metadata) ───── */}
        <TabsContent value="episodes" className="space-y-6 mt-4">
          <Card className="rounded-2xl">
            <CardHeader><CardTitle className="font-heading text-lg">Course Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Title</label>
                  <Input value={meta.title || ''} onChange={(e) => setMeta((p) => ({ ...p, title: e.target.value }))} data-testid="course-title-input" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Type</label>
                  <Select value={meta.course_type || 'prenatal'} onValueChange={(v) => setMeta((p) => ({ ...p, course_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prenatal">Prenatal</SelectItem>
                      <SelectItem value="postnatal">Postnatal</SelectItem>
                      <SelectItem value="ttc">TTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* V14.1 — Trimester / Stage chips. Multi-select. */}
              <div>
                <label className="text-sm font-medium mb-1.5 block">Audience stage</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: 't1', label: 'Trimester 1' },
                    { v: 't2', label: 'Trimester 2' },
                    { v: 't3', label: 'Trimester 3' },
                    { v: 'postnatal', label: 'Postnatal' },
                    { v: 'ttc', label: 'TTC' },
                  ].map((opt) => {
                    const tags = Array.isArray(meta.trimester_tags) ? meta.trimester_tags : [];
                    const selected = tags.includes(opt.v);
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${selected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted/50'}`}
                        onClick={() => {
                          const next = selected ? tags.filter((x) => x !== opt.v) : [...tags, opt.v];
                          setMeta((p) => ({ ...p, trimester_tags: next }));
                        }}
                        data-testid={`trim-chip-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Select one or more stages this course is meant for. Replaces the legacy single-trimester field.</p>
              </div>

              {/* V14.1 — Auto-generated LMS SKU (read-only — admin copies into OPS panel) */}
              <div>
                <label className="text-sm font-medium mb-1.5 block flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5" />LMS SKU (copy this into OPS)
                </label>
                <div className="flex gap-2">
                  <Input value={meta.ops_sku || ''} readOnly className="font-mono text-xs bg-muted/40" data-testid="course-ops-sku-display" />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => { navigator.clipboard.writeText(meta.ops_sku || ''); toast.success('SKU copied'); }}>
                    Copy
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Auto-generated. Sales adds this SKU into the OPS catalog along with the Paid Trial Fee and Full Course Fee. The OPS panel then drives purchases on web/app/LMS.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Description</label>
                <Textarea value={meta.description || ''} onChange={(e) => setMeta((p) => ({ ...p, description: e.target.value }))} rows={3} data-testid="course-desc-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Thumbnail</label>
                <div className="flex items-center gap-3">
                  {course.thumbnail_path ? (
                    <img src={`/api/courses/${courseId}/thumbnail`} alt="Thumbnail" className="h-20 w-32 object-cover rounded-lg border" />
                  ) : (
                    <div className="h-20 w-32 rounded-lg border border-dashed flex items-center justify-center bg-muted/30">
                      <Image className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <input type="file" accept="image/*" className="hidden" id="thumb-upload" onChange={(e) => e.target.files[0] && uploadThumbnail(e.target.files[0])} />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('thumb-upload').click()} disabled={thumbnailUploading}>
                      {thumbnailUploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Image className="h-3.5 w-3.5 mr-1.5" />}
                      {course.thumbnail_path ? 'Replace' : 'Upload'}
                    </Button>
                    <p className="text-[10px] text-muted-foreground mt-1">Max 5MB.</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => saveAll(meta)} disabled={saving} data-testid="save-course-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><Video className="h-5 w-5" />Episodes ({episodes.length})</CardTitle>
                <div className="flex items-center gap-2">
                  {episodes.some((ep) => ep.status === 'encoding' || ep.status === 'uploading') && (
                    <Button size="sm" variant="outline" onClick={refreshEncoding} data-testid="refresh-encoding-btn">
                      <Loader2 className="h-3.5 w-3.5 mr-1" />Refresh status
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setAddEpDialog(true)} data-testid="add-episode-btn">
                    <Plus className="h-4 w-4 mr-1" />Add Episode
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {episodes.length === 0 ? (
                <div className="text-center py-10">
                  <Video className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No episodes yet</p>
                  <Button variant="outline" className="mt-3" onClick={() => setAddEpDialog(true)}>Add your first episode</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {episodes.map((ep, idx) => {
                    const upState = uploading[ep.id];
                    const effectiveStatus = upState?.status || ep.status;
                    return (
                      <div
                        key={ep.id}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, idx)}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${draggedIdx === idx ? 'opacity-50 border-primary' : 'hover:bg-muted/20'}`}
                        data-testid={`episode-row-${ep.id}`}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab flex-shrink-0" />
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-primary">{idx + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{ep.title}</p>
                          {ep.description && <p className="text-xs text-muted-foreground truncate">{ep.description}</p>}
                          {effectiveStatus === 'uploading' && upState && (<Progress value={upState.progress} className="h-1.5 mt-1.5" />)}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusIcon(effectiveStatus)}
                          <Badge variant="outline" className="text-[10px]">{effectiveStatus?.replace(/_/g, ' ')}</Badge>
                          {ep.duration_minutes && <span className="text-xs text-muted-foreground">{ep.duration_minutes} min</span>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {(effectiveStatus === 'awaiting_upload' || effectiveStatus === 'failed') && (
                            <>
                              <input
                                ref={(el) => fileInputRefs.current[ep.id] = el}
                                type="file" accept="video/*" className="hidden"
                                onChange={(e) => e.target.files[0] && uploadVideo(ep.id, ep.bunny_guid, e.target.files[0])}
                              />
                              <Button variant="outline" size="sm" onClick={() => fileInputRefs.current[ep.id]?.click()} data-testid={`upload-btn-${ep.id}`}>
                                <Upload className="h-3.5 w-3.5 mr-1" />Upload
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => { setEditEpDialog({ open: true, ep }); setEditEpForm({ title: ep.title, description: ep.description || '' }); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteEpisode(ep.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ───── TAB 2: ACCESS & PRICING ───── */}
        <TabsContent value="access" className="space-y-5 mt-4">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lock className="h-4 w-4" />Access Tiers
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Configure what learners get at each tier. OPS SKUs are the contract
                with the Payments panel — the price shown comes from OPS, not here.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {tiers.map((t, idx) => (
                <div key={t.key} className="p-4 border rounded-xl space-y-3" data-testid={`tier-row-${t.key}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{t.label}</span>
                      <p className="text-[11px] text-muted-foreground">
                        {t.key === 'free_trial' && 'First N episodes, no payment required'}
                        {t.key === 'paid_trial' && 'Pay once, unlock first N episodes'}
                        {t.key === 'full' && 'Unlock every episode in this course'}
                      </p>
                    </div>
                    <Switch
                      checked={!!t.enabled}
                      onCheckedChange={(v) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, enabled: v } : x))}
                      data-testid={`tier-enabled-${t.key}`}
                    />
                  </div>
                  {t.enabled && (
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[11px] font-medium mb-1 block">Label</label>
                        <Input value={t.label}
                          onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
                      </div>
                      <div>
                        <label className="text-[11px] font-medium mb-1 block">CTA text</label>
                        <Input value={t.cta || ''}
                          onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, cta: e.target.value } : x))} />
                      </div>
                      {(t.key === 'free_trial' || t.key === 'paid_trial') ? (
                        <div>
                          <label className="text-[11px] font-medium mb-1 block">Episode count</label>
                          <Input type="number" min={1} value={t.episode_count || 1}
                            onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, episode_count: parseInt(e.target.value) || 1 } : x))}
                            data-testid={`tier-eps-${t.key}`} />
                        </div>
                      ) : (
                        <div>
                          <label className="text-[11px] font-medium mb-1 block">Full course OPS SKU</label>
                          <Input value={t.ops_sku || ''} placeholder="e.g. garbhsanskar_full"
                            onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, ops_sku: e.target.value } : x))} />
                        </div>
                      )}
                      {t.key === 'paid_trial' && (
                        <div className="sm:col-span-3">
                          <label className="text-[11px] font-medium mb-1 block">Paid trial OPS SKU</label>
                          <Input value={t.ops_sku || ''} placeholder="e.g. garbhsanskar_addon"
                            onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, ops_sku: e.target.value } : x))} />
                        </div>
                      )}
                      {/* V14.5 Part 19w — per-tier price so the learner course
                          detail page can render an actual Razorpay "Buy"
                          button. Empty/0 means the tier is configured but
                          NOT purchasable (admin still working on it) — the
                          learner sees the "Unlock Elite Program" CTA. */}
                      {(t.key === 'paid_trial' || t.key === 'full') && (
                        <>
                          <div>
                            <label className="text-[11px] font-medium mb-1 block">Price (₹)</label>
                            <Input type="number" min={0} step="1"
                              value={t.price_inr ?? ''}
                              placeholder={t.key === 'paid_trial' ? '199' : '999'}
                              onChange={(e) => {
                                const newPrice = e.target.value === '' ? null : (parseInt(e.target.value) || 0);
                                setTiers((arr) => arr.map((x, i) => {
                                  if (i !== idx) return x;
                                  // V14.5 Part 19z6 — Founder bug (2026-05-21):
                                  // admin changed price_inr from 199 → 1099 but
                                  // the displayed CTA stayed "Buy for ₹999"
                                  // because `cta` is a stored string. Auto-sync
                                  // the cta when it matches the default pattern
                                  // ("Try for ₹NNN" / "Buy for ₹NNN"); preserve
                                  // it if admin has authored a custom string.
                                  const defaultCtaRe = /^(Try for|Buy for) ₹\d+$/;
                                  let cta = x.cta || '';
                                  if (!cta || defaultCtaRe.test(cta)) {
                                    cta = newPrice
                                      ? (x.key === 'paid_trial' ? `Try for ₹${newPrice}` : `Buy for ₹${newPrice}`)
                                      : '';
                                  }
                                  return { ...x, price_inr: newPrice, cta };
                                }));
                              }}
                              data-testid={`tier-price-${t.key}`} />
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Razorpay amount the learner is charged. CTA text auto-updates.
                            </p>
                          </div>
                          {/* V14.5 Part 19x — App-team handover: optional MRP
                              (strike-through "₹499 ₹199") for marketing. */}
                          <div className={t.key === 'paid_trial' ? 'sm:col-span-2' : ''}>
                            <label className="text-[11px] font-medium mb-1 block">MRP (₹) <span className="text-muted-foreground font-normal">— optional, shown struck through</span></label>
                            <Input type="number" min={0} step="1"
                              value={t.mrp_inr ?? ''}
                              placeholder={t.key === 'paid_trial' ? '499' : '4999'}
                              onChange={(e) => setTiers((arr) => arr.map((x, i) => i === idx ? { ...x, mrp_inr: e.target.value === '' ? null : (parseInt(e.target.value) || 0) } : x))}
                              data-testid={`tier-mrp-${t.key}`} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <Button onClick={() => saveAll({ access_tiers: tiers })} disabled={saving} data-testid="save-tiers-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save tiers
              </Button>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Ticket expiry</CardTitle>
              <p className="text-xs text-muted-foreground">When a learner's access runs out.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Expiry policy</label>
                <Select value={expiry.type || 'never'} onValueChange={(v) => setExpiry((p) => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="expiry-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never expires</SelectItem>
                    <SelectItem value="edd_plus_days">EDD + N days (pregnancy-bound)</SelectItem>
                    <SelectItem value="days_from_grant">N days from grant</SelectItem>
                    <SelectItem value="fixed_date">Fixed calendar date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {expiry.type === 'edd_plus_days' && (
                <div>
                  <label className="text-xs font-medium mb-1 block">Buffer days after EDD</label>
                  <Input type="number" min={0} value={expiry.buffer_days || 0}
                    onChange={(e) => setExpiry((p) => ({ ...p, buffer_days: parseInt(e.target.value) || 0 }))}
                    data-testid="expiry-buffer-input" />
                </div>
              )}
              {expiry.type === 'days_from_grant' && (
                <div>
                  <label className="text-xs font-medium mb-1 block">Days from grant</label>
                  <Input type="number" min={1} value={expiry.days || 365}
                    onChange={(e) => setExpiry((p) => ({ ...p, days: parseInt(e.target.value) || 365 }))} />
                </div>
              )}
              {expiry.type === 'fixed_date' && (
                <div>
                  <label className="text-xs font-medium mb-1 block">Fixed date</label>
                  <Input type="date" value={(expiry.datetime || '').split('T')[0] || ''}
                    onChange={(e) => setExpiry((p) => ({ ...p, datetime: e.target.value ? `${e.target.value}T23:59:59Z` : null }))} />
                </div>
              )}
              <Button onClick={() => saveAll({ expiry_policy: expiry })} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />Save expiry
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ───── TAB 3: PACING & DISCOVERY ───── */}
        <TabsContent value="pacing" className="space-y-5 mt-4">
          {/* V14.1 — Unified Unlock Rule (replaces complex pacing for new courses) */}
          <Card className="rounded-2xl" data-testid="unlock-rule-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lock className="h-4 w-4" />Unlock rule
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">V14.1</Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                When a learner is granted this course (via OPS purchase or direct sales assignment), this rule decides whether
                they get instant access OR a greyed-out "unlocks at week 32" card.
                Replaces the old per-episode pacing rules for new courses.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Strategy</label>
                <Select value={unlockRule.type || 'immediate'} onValueChange={(v) => {
                  // Reset rule body when changing type
                  if (v === 'immediate') setUnlockRule({ type: 'immediate' });
                  else if (v === 'week_gte') setUnlockRule({ type: 'week_gte', week: 32 });
                }}>
                  <SelectTrigger data-testid="unlock-rule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate — open the moment access is granted</SelectItem>
                    <SelectItem value="week_gte">Week-gated — unlocks at pregnancy week N</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {unlockRule.type === 'week_gte' && (
                <div>
                  <label className="text-xs font-medium mb-1 block">Pregnancy week (1–45)</label>
                  <Input type="number" min={1} max={45} value={unlockRule.week ?? ''}
                    onChange={(e) => setUnlockRule({ type: 'week_gte', week: parseInt(e.target.value) || 1 })}
                    data-testid="unlock-rule-week-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Locked card will say e.g. <em>"Unlocks at pregnancy week 32 (about 4 weeks away)"</em>.
                  </p>
                </div>
              )}
              <Button onClick={() => saveAll({ unlock_rule: unlockRule })} disabled={saving} data-testid="save-unlock-rule-btn">
                <Save className="h-4 w-4 mr-2" />Save unlock rule
              </Button>
            </CardContent>
          </Card>

          {/* V14.2 — Legacy pacing card removed entirely. Unlock Rule above
              is the only authoritative pacing config now. Old fields
              (linear/drip/calendar/hybrid) are no longer surfaced — the
              backend still ignores them gracefully if any old course doc
              still has them. */}

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="h-4 w-4" />Discovery
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Who sees this course in the Learn tab.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Listed in Learn tab</label>
                  <p className="text-[11px] text-muted-foreground">Turn off to make course only accessible via direct link or gift.</p>
                </div>
                <Switch checked={!!discovery.is_listed}
                  onCheckedChange={(v) => setDiscovery((p) => ({ ...p, is_listed: v }))}
                  data-testid="discovery-listed-switch" />
              </div>

              {/* V14.5 Part 19u — Audience as stage checkboxes (prenatal/postnatal) */}
              <div>
                <label className="text-xs font-medium mb-1.5 block">Audience</label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  Pick which stage(s) this course is relevant for. Leave both unchecked to show to all stages.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 'prenatal', label: 'Prenatal' },
                    { value: 'postnatal', label: 'Postnatal' },
                  ].map((opt) => {
                    const selected = (discovery.listed_in_audiences || []).includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                          selected
                            ? 'bg-rose-50 border-rose-300 text-rose-800'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                        data-testid={`discovery-audience-${opt.value}`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={selected}
                          onChange={(e) => {
                            const current = new Set(discovery.listed_in_audiences || []);
                            if (e.target.checked) current.add(opt.value);
                            else current.delete(opt.value);
                            setDiscovery((p) => ({
                              ...p,
                              listed_in_audiences: Array.from(current),
                            }));
                          }}
                        />
                        <span
                          className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center ${
                            selected ? 'bg-rose-500 border-rose-500' : 'border-slate-300'
                          }`}
                          aria-hidden
                        >
                          {selected ? <CheckCircle2 className="h-3 w-3 text-white" /> : null}
                        </span>
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* V14.5 Part 19u — Learner type target (free / paid / elite / all) */}
              <div>
                <label className="text-xs font-medium mb-1.5 block">Learner type</label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  The minimum learner tier that can see this course. Higher tiers always see lower-tier courses.
                </p>
                <Select
                  value={discovery.learner_type_target || 'all'}
                  onValueChange={(v) => setDiscovery((p) => ({ ...p, learner_type_target: v }))}
                >
                  <SelectTrigger data-testid="discovery-learner-type-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All learners</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={() => saveAll({ discovery })} disabled={saving} data-testid="discovery-save-btn">
                <Save className="h-4 w-4 mr-2" />Save discovery
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Episode Dialog */}
      <Dialog open={addEpDialog} onOpenChange={setAddEpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Episode</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Title</label>
              <Input value={newEp.title} onChange={(e) => setNewEp((p) => ({ ...p, title: e.target.value }))} placeholder="Episode title" data-testid="new-ep-title" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea value={newEp.description} onChange={(e) => setNewEp((p) => ({ ...p, description: e.target.value }))} placeholder="Brief description" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEpDialog(false)}>Cancel</Button>
            <Button onClick={addEpisode} data-testid="create-ep-btn">Create Episode</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog open={editEpDialog.open} onOpenChange={(o) => setEditEpDialog({ open: o, ep: editEpDialog.ep })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Episode</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Title</label>
              <Input value={editEpForm.title} onChange={(e) => setEditEpForm((p) => ({ ...p, title: e.target.value }))} data-testid="edit-ep-title" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea value={editEpForm.description} onChange={(e) => setEditEpForm((p) => ({ ...p, description: e.target.value }))} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEpDialog({ open: false, ep: null })}>Cancel</Button>
            <Button onClick={saveEpisodeEdit} data-testid="save-ep-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PreviewAsLearnerDialog
        open={simOpen}
        onClose={() => setSimOpen(false)}
        courseId={courseId}
      />
    </div>
  );
}

// ───── Helpers ─────

function WeekGateEditor({ value, onChange, episodes }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium">Pregnancy week per episode</p>
      {episodes.map((ep) => (
        <div key={ep.id} className="flex items-center gap-3">
          <span className="text-[11px] w-24 truncate">EP {ep.display_order}. {ep.title}</span>
          <Input
            type="number" min={1} max={45}
            placeholder="week"
            className="w-24"
            value={value[String(ep.display_order)] || value[ep.display_order] || ''}
            onChange={(e) => {
              const v = parseInt(e.target.value) || null;
              const next = { ...value };
              if (v) next[String(ep.display_order)] = v;
              else delete next[String(ep.display_order)];
              onChange(next);
            }}
            data-testid={`week-gate-ep-${ep.display_order}`}
          />
        </div>
      ))}
    </div>
  );
}

function CalendarEditor({ value, onChange, episodes }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium">Unlock datetime per episode</p>
      {episodes.map((ep) => {
        const entry = (value || []).find((c) => c.episode === ep.display_order);
        return (
          <div key={ep.id} className="flex items-center gap-3">
            <span className="text-[11px] w-24 truncate">EP {ep.display_order}. {ep.title}</span>
            <Input
              type="datetime-local"
              value={(entry?.datetime || '').replace('Z', '').slice(0, 16)}
              onChange={(e) => {
                const dt = e.target.value ? `${e.target.value}:00Z` : '';
                const rest = (value || []).filter((c) => c.episode !== ep.display_order);
                if (dt) onChange([...rest, { episode: ep.display_order, datetime: dt }]);
                else onChange(rest);
              }}
              className="max-w-sm"
              data-testid={`cal-ep-${ep.display_order}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function PreviewAsLearnerDialog({ open, onClose, courseId }) {
  const [params, setParams] = useState({
    tier: 'full', stage: 'prenatal', pregnancy_week: 20, granted_days_ago: 0,
    unlock_at_pregnancy_week: '', postnatal_days_ago: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/admin/access/simulate', {
        course_id: courseId,
        tier: params.tier,
        stage: params.stage,
        pregnancy_week: params.stage === 'postnatal' ? null : (parseInt(params.pregnancy_week) || null),
        granted_days_ago: parseInt(params.granted_days_ago) || 0,
        unlock_at_pregnancy_week: params.unlock_at_pregnancy_week
          ? parseInt(params.unlock_at_pregnancy_week) : null,
        postnatal_days_ago: params.stage === 'postnatal' ? (parseInt(params.postnatal_days_ago) || 0) : null,
      });
      setResult(res.data);
    } catch {
      toast.error('Simulation failed');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="preview-as-learner-dialog">
        <DialogHeader>
          <DialogTitle>Preview as learner</DialogTitle>
          <p className="text-xs text-muted-foreground">
            See exactly what a learner with this profile would see — no data is modified.
          </p>
        </DialogHeader>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] font-medium">Tier</label>
            <Select value={params.tier} onValueChange={(v) => setParams((p) => ({ ...p, tier: v }))}>
              <SelectTrigger data-testid="sim-tier-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No ticket</SelectItem>
                <SelectItem value="free_trial">Free trial</SelectItem>
                <SelectItem value="paid_trial">Paid trial</SelectItem>
                <SelectItem value="full">Full</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-medium">Stage</label>
            <Select value={params.stage} onValueChange={(v) => setParams((p) => ({ ...p, stage: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prenatal">Prenatal</SelectItem>
                <SelectItem value="postnatal">Postnatal</SelectItem>
                <SelectItem value="ttc">TTC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-medium">
              {params.stage === 'postnatal' ? 'Days post-delivery' : 'Pregnancy week'}
            </label>
            {params.stage === 'postnatal' ? (
              <Input type="number" min={0} value={params.postnatal_days_ago}
                onChange={(e) => setParams((p) => ({ ...p, postnatal_days_ago: e.target.value }))}
                data-testid="sim-postnatal-days-input" />
            ) : (
              <Input type="number" min={1} max={45} value={params.pregnancy_week}
                onChange={(e) => setParams((p) => ({ ...p, pregnancy_week: e.target.value }))}
                data-testid="sim-week-input" />
            )}
          </div>
          <div>
            <label className="text-[11px] font-medium">Granted N days ago</label>
            <Input type="number" min={0} value={params.granted_days_ago}
              onChange={(e) => setParams((p) => ({ ...p, granted_days_ago: e.target.value }))} />
          </div>
        </div>
        <Button onClick={run} disabled={loading} data-testid="sim-run-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}Run simulation
        </Button>
        {result && (
          <div className="border rounded-xl p-3 space-y-2 max-h-72 overflow-auto" data-testid="sim-result">
            <div className="text-xs flex items-start gap-2">
              <span>Course access:</span>
              {result.course_access?.allowed
                ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">OPEN</Badge>
                : <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300" data-testid="sim-lock-reason">{result.course_access?.lock_reason}</Badge>}
            </div>
            {result.course_access?.lock_message && !result.course_access?.allowed && (
              <p className="text-[11px] text-muted-foreground italic" data-testid="sim-lock-message">
                {result.course_access.lock_message}
              </p>
            )}
            {result.episodes?.map((ep) => (
              <div key={ep.id} className="text-[11px] flex items-center justify-between border-b py-1">
                <span>EP {ep.display_order}. {ep.title}</span>
                <span>
                  {ep.access?.allowed
                    ? <span className="text-emerald-700 dark:text-emerald-400">✓ open</span>
                    : <span className="text-slate-600 dark:text-slate-400">🔒 {ep.access?.lock_reason} — {ep.access?.lock_message}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ───── Unlock Timeline (Phase C+) ─────
// Renders a horizontal timeline showing WHEN each episode flips open for a
// hypothetical learner who buys today. Catches mis-configured drip schedules
// (e.g. Episode 9 opens on day 24 but the course is pregnancy-week 22 gated).

function UnlockTimelineCard({ courseId, pacing, episodes }) {
  const [tier, setTier] = useState('full');
  const [preWeek, setPreWeek] = useState(20);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  const strategy = pacing?.strategy || 'immediate';
  const needsWeek = strategy === 'week_gated' || strategy === 'hybrid';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/access/courses/${courseId}/unlock-timeline`, {
        params: { tier, pregnancy_week: needsWeek ? preWeek : undefined },
      });
      setTimeline(res.data);
    } catch {
      toast.error('Could not compute timeline');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, JSON.stringify(pacing), tier, preWeek]);

  const maxDay = timeline?.total_days_to_fully_open || 0;
  const scaleDays = Math.max(maxDay, 14); // at least 2 weeks wide

  return (
    <Card className="rounded-2xl" data-testid="unlock-timeline-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <CalendarClockIcon />Unlock timeline preview
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          What a learner buying today sees. Catches mis-configured drip/week rules visually.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-36">
            <label className="text-[11px] font-medium mb-1 block">Tier</label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="h-8" data-testid="timeline-tier-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="free_trial">Free trial</SelectItem>
                <SelectItem value="paid_trial">Paid trial</SelectItem>
                <SelectItem value="full">Full</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {needsWeek && (
            <div className="w-28">
              <label className="text-[11px] font-medium mb-1 block">Preg. week</label>
              <Input type="number" min={1} max={45} value={preWeek}
                className="h-8"
                onChange={(e) => setPreWeek(parseInt(e.target.value) || 20)}
                data-testid="timeline-week-input" />
            </div>
          )}
          <div className="text-[11px] text-muted-foreground ml-auto">
            {loading ? 'Computing…' : (
              timeline?.total_days_to_fully_open === 0
                ? 'All episodes open on Day 0'
                : `Fully opens by day ${timeline?.total_days_to_fully_open}`
            )}
          </div>
        </div>

        <div className="relative border-b pb-1 mb-2 text-[10px] text-muted-foreground">
          <div className="flex justify-between">
            {Array.from({ length: 6 }, (_, i) => {
              const d = Math.round((i * scaleDays) / 5);
              return <span key={i}>{d === 0 ? 'Today' : `Day ${d}`}</span>;
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          {(timeline?.episodes || []).map((ep) => {
            const unlockDay = ep.unlock_day;
            const leftPct = unlockDay == null ? 100 : Math.min(100, (unlockDay / scaleDays) * 100);
            const barColor = ep.blocks_on === 'UPGRADE_REQUIRED'
              ? 'bg-amber-300'
              : ep.blocks_on === 'LINEAR_LOCKED'
              ? 'bg-slate-300'
              : ep.is_now
              ? 'bg-emerald-400'
              : 'bg-rose-300';
            return (
              <div key={ep.display_order} className="flex items-center gap-2 text-[11px]"
                   data-testid={`timeline-ep-${ep.display_order}`}>
                <span className="w-28 truncate" title={ep.title}>
                  <span className="font-medium">EP {ep.display_order}.</span> {ep.title}
                </span>
                <div className="flex-1 relative h-3 bg-slate-100 rounded-sm overflow-hidden">
                  {unlockDay != null && unlockDay <= scaleDays && (
                    <div
                      className={`absolute top-0 bottom-0 ${barColor} opacity-70`}
                      style={{ left: `${leftPct}%`, right: 0 }}
                      title={ep.note || ''}
                    />
                  )}
                  {unlockDay != null && unlockDay <= scaleDays && (
                    <div
                      className="absolute top-0 bottom-0 w-[2px] bg-slate-900"
                      style={{ left: `${leftPct}%` }}
                    />
                  )}
                </div>
                <span className="w-28 text-right text-muted-foreground">
                  {ep.is_now
                    ? <span className="text-emerald-700 font-medium">Day 0 · open</span>
                    : unlockDay == null
                    ? <span className="text-amber-700">{ep.blocks_on || 'locked'}</span>
                    : <span>Day {unlockDay}{ep.blocks_on === 'LINEAR_LOCKED' ? ' +linear' : ''}</span>}
                </span>
              </div>
            );
          })}
          {!(timeline?.episodes || []).length && !loading && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No episodes to visualize — add some in the Episodes tab first.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><i className="w-2 h-2 bg-emerald-400 inline-block rounded-sm"/>Opens today</span>
          <span className="flex items-center gap-1"><i className="w-2 h-2 bg-rose-300 inline-block rounded-sm"/>Drip/date lock</span>
          <span className="flex items-center gap-1"><i className="w-2 h-2 bg-slate-300 inline-block rounded-sm"/>Linear (progress-based)</span>
          <span className="flex items-center gap-1"><i className="w-2 h-2 bg-amber-300 inline-block rounded-sm"/>Upgrade required</span>
        </div>
      </CardContent>
    </Card>
  );
}

function CalendarClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h6"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
      <circle cx="17" cy="17" r="4"/><path d="M17 15v2l1.5 1.5"/>
    </svg>
  );
}
